import {
  FEEDBACK_ATTACHMENT_JPEG_QUALITY,
  FEEDBACK_ATTACHMENT_MAX_EDGE_PX,
  computeDownscaledSize,
  shouldKeepOriginalImage,
} from "./feedback_attachment_utils";

export interface PreparedFeedbackImage {
  /** Full data URL (thumbnail); the raw base64 is everything after the comma. */
  dataUrl: string;
  contentType: "image/jpeg" | "image/png";
  width: number;
  height: number;
}

/** Decodes an image file into something `drawImage` accepts. */
async function decodeImage(file: Blob): Promise<{
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file);
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
    } catch {
      // Fall through to the <img> path (some browsers reject certain types here).
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(file);
  });
}

/**
 * Prepares an image the user attached to the feedback dialog for sending:
 * - a small PNG (≤ 2 MB, ≤ 2560 px) is sent unchanged — UI screenshots stay
 *   lossless and readable;
 * - everything else is re-encoded as JPEG (quality 0.85) with the long edge
 *   capped at 2560 px — 4K screenshots or phone photos otherwise weigh
 *   several MB each, and re-encoding also drops photo metadata (GPS …).
 *   Transparent areas are painted white, since JPEG has no alpha channel and
 *   would render them black.
 *
 * Throws if the browser cannot decode the file or it has no pixel size
 * (e.g. HEIC outside Safari, an SVG without dimensions).
 */
export async function prepareFeedbackImage(file: File): Promise<PreparedFeedbackImage> {
  const decoded = await decodeImage(file);
  try {
    if (!(decoded.width > 0) || !(decoded.height > 0)) {
      throw new Error("Image has no pixel size");
    }
    if (
      shouldKeepOriginalImage({
        type: file.type,
        byteSize: file.size,
        width: decoded.width,
        height: decoded.height,
      })
    ) {
      const dataUrl = await readAsDataUrl(file);
      if (dataUrl.startsWith("data:image/png;base64,")) {
        return { dataUrl, contentType: "image/png", width: decoded.width, height: decoded.height };
      }
      // Unexpected data URL shape — fall back to re-encoding below.
    }

    const { width, height } = computeDownscaledSize(
      decoded.width,
      decoded.height,
      FEEDBACK_ATTACHMENT_MAX_EDGE_PX,
    );
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(decoded.source, 0, 0, width, height);
    const dataUrl = canvas.toDataURL("image/jpeg", FEEDBACK_ATTACHMENT_JPEG_QUALITY);
    if (!dataUrl.startsWith("data:image/jpeg;base64,")) {
      throw new Error("JPEG encoding failed");
    }
    return { dataUrl, contentType: "image/jpeg", width, height };
  } finally {
    decoded.release();
  }
}
